/**
 * Shared evidence-tag helpers for structured artifacts. HUEM tools annotate
 * fields with an evidence tag on the label line — `*(Confirmed)*`, `*(Hypothesis)*`
 * or `*(Assumed)*` — which drives both the tag badge and the derived score /
 * gaps count on cards whose markdown carries no explicit number (e.g. JTBD,
 * whose contract forbids a score in the artifact).
 */

export type EvidenceTag = 'Confirmed' | 'Hypothesis' | 'Assumed';

/** Confidence weight per tag — feeds the derived score. */
const WEIGHT: Record<EvidenceTag, number> = {
  Confirmed: 1,
  Hypothesis: 0,
  Assumed: 0.5,
};

/** Normalise a raw tag string (from `*(...)*`) to an EvidenceTag, or null. */
export const parseEvidenceTag = (raw: string | null | undefined): EvidenceTag | null => {
  if (!raw) return null;
  const t = raw.trim().toLowerCase();
  if (t.startsWith('ground')) return 'Confirmed';
  if (t.startsWith('assum')) return 'Hypothesis';
  if (t.startsWith('infer')) return 'Assumed';
  return null;
};

/**
 * Inverse of {@link parseEvidenceTag}: map a display EvidenceTag back to the
 * source vocabulary the artifacts are stored in (`Grounded` / `Assumed` /
 * `Inferred`). Serializers MUST use this so an edit round-trips — writing the
 * display word ("Confirmed") straight to `evidence_tag` would fail re-parse and
 * silently drop every tag (score → 0, all fields become gaps).
 */
const SOURCE_TAG: Record<EvidenceTag, string> = {
  Confirmed: 'Grounded',
  Hypothesis: 'Assumed',
  Assumed: 'Inferred',
};
export const serializeEvidenceTag = (tag: EvidenceTag | null): string =>
  SOURCE_TAG[tag ?? 'Hypothesis'];

/** Minimal shape any evidence-tagged field satisfies. */
export interface Taggable {
  tag: EvidenceTag | null;
}

/**
 * Derive a 0–100 confidence score from a set of tagged fields
 * (Confirmed = full, Inferred = half, Hypothesis / untagged = none). Used when the
 * artifact markdown carries no explicit score.
 */
export const weightedScore = (fields: Taggable[]): number => {
  if (!fields.length) return 0;
  const sum = fields.reduce((n, f) => n + (f.tag ? WEIGHT[f.tag] : 0), 0);
  return Math.round((sum / fields.length) * 100);
};

/** Count fields that are not yet Confirmed — the "N gaps to fulfil" figure. */
export const gapsCount = (fields: Taggable[]): number =>
  fields.reduce((n, f) => n + (f.tag === 'Confirmed' ? 0 : 1), 0);
